/**
 * Mixed Arabic/English text reads in order everywhere in the app, not only in
 * Blobby's Markdown answers.
 *
 * A task title like "مراجعة Perla: 9 تاسك" on an English page ran
 * left-to-right, with "Perla: 9" glued together. Every title, name, comment
 * and label had the same problem, and they render in hundreds of places. So
 * instead of fixing each place, this watches the page and, for each text
 * that mixes the two scripts (or runs against the page), inserts the
 * invisible Unicode isolates that keep it in order (isolateText in
 * @teamblob/ui text-direction.ts):
 *
 * - A text node that is all of its element's text (`<span>{task.title}</span>`)
 *   is a value of its own and runs its own direction. A text node next to
 *   other text (around a link, after a label) is a piece of a sentence: it
 *   keeps the sentence's direction and only its phrases in the other script
 *   are sealed. `data-bidi="sentence"` asks for the latter on text that
 *   builds an English sentence around values in one string.
 * - Multi-line text (`whitespace-pre-wrap` / `-line`: comments, messages,
 *   notes) also gets `dir` on its element, so an Arabic comment aligns right.
 * - Left alone: anything under `data-bidi="off"` (Markdown seals its own),
 *   editors, form fields, code and SVG.
 *
 * Only the characters of text nodes change, never the elements React
 * renders, so React's next update simply overwrites them and the observer
 * seals the new text. Text that already carries bidi controls is left as it
 * is. Copying strips the controls again (onCopy).
 */

import {
  hasBidiControls,
  hasRtlLetters,
  isolateText,
  stripBidiControls,
  textDirection,
  type TextDirection,
} from "@teamblob/ui/components";

const LETTER = /\p{L}/u;
/** Not ours to touch: sealed elsewhere, edited, or not prose. */
const SKIP = [
  '[data-bidi="off"]',
  "[contenteditable]",
  ".ProseMirror",
  ...["script", "style", "noscript", "textarea", "input", "select", "option"],
  ...["code", "pre", "kbd", "samp", "svg", "math"],
].join(", ");
/** Multi-line text, which aligns by its own direction. */
const PARAGRAPH = ".whitespace-pre-wrap, .whitespace-pre-line";
/** Marks a `dir` this pass set, so it can change it when the text does. */
const OWN_DIR = "data-bidi-dir";

/** The direction the element's text runs in. */
function directionOf(el: Element): TextDirection {
  const dir = el.closest("[dir]")?.getAttribute("dir")?.toLowerCase();
  if (dir === "rtl" || dir === "ltr") return dir;
  return getComputedStyle(el).direction === "rtl" ? "rtl" : "ltr";
}

/** The node is all of its element's text: no sibling holds any. */
function isWhole(node: Text): boolean {
  for (const sibling of node.parentNode?.childNodes ?? []) {
    if (sibling !== node && (sibling.textContent ?? "").trim() !== "") {
      return false;
    }
  }
  return true;
}

/** Gives a paragraph its text's direction, unless its author chose one. */
function alignParagraph(el: Element, text: string) {
  const ours = el.hasAttribute(OWN_DIR);
  if (el.hasAttribute("dir") && !ours) return;
  const dir = textDirection(text);
  if (dir !== undefined) {
    el.setAttribute("dir", dir);
    el.setAttribute(OWN_DIR, "");
  } else if (ours) {
    el.removeAttribute("dir");
    el.removeAttribute(OWN_DIR);
  }
}

/** Seals one text node in place, when it needs it. */
export function sealTextNode(node: Text): void {
  const value = node.nodeValue ?? "";
  if (!LETTER.test(value) || hasBidiControls(value)) return;
  const parent = node.parentElement;
  if (parent === null) return;
  // The usual case, English on an English page: nothing to do.
  if (
    !hasRtlLetters(value) &&
    parent.closest('[dir="rtl"], [dir="auto"]') === null
  ) {
    return;
  }
  if (parent.closest(SKIP) !== null) return;
  const whole =
    parent.closest('[data-bidi="sentence"]') === null && isWhole(node);
  if (whole && parent.matches(PARAGRAPH)) alignParagraph(parent, value);
  const sealed = isolateText(value, directionOf(parent), whole);
  if (sealed !== value) node.nodeValue = sealed;
}

/** Seals every text node in a subtree. */
function sealAll(root: Node) {
  if (root.nodeType === Node.TEXT_NODE) {
    sealTextNode(root as Text);
    return;
  }
  if (root.nodeType !== Node.ELEMENT_NODE) return;
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  for (let node = walker.nextNode(); node !== null; node = walker.nextNode()) {
    sealTextNode(node as Text);
  }
}

/** Copied text leaves without the isolates. */
export function onCopy(event: ClipboardEvent): void {
  const selection = document.getSelection();
  const data = event.clipboardData;
  if (event.defaultPrevented || selection === null || data === null) return;
  const text = selection.toString();
  if (!hasBidiControls(text)) return;
  const holder = document.createElement("div");
  for (let i = 0; i < selection.rangeCount; i += 1) {
    holder.append(selection.getRangeAt(i).cloneContents());
  }
  data.setData("text/plain", stripBidiControls(text));
  data.setData("text/html", stripBidiControls(holder.innerHTML));
  event.preventDefault();
}

/**
 * Seals what is on the page now and everything rendered later. Returns a
 * function that stops it.
 */
export function startBidiText(root: HTMLElement = document.body): () => void {
  sealAll(root);
  const observer = new MutationObserver((records) => {
    for (const record of records) {
      if (record.type === "characterData") sealAll(record.target);
      else record.addedNodes.forEach(sealAll);
    }
  });
  observer.observe(root, {
    childList: true,
    characterData: true,
    subtree: true,
  });
  document.addEventListener("copy", onCopy);
  return () => {
    observer.disconnect();
    document.removeEventListener("copy", onCopy);
  };
}
